/**
 * `IconButton`'s look (DESIGN_SYSTEM.md §5.2): a square control holding one icon, in three variants and the
 * Button's two heights. `root` is the `<button>`: layout only (§4.2); the paint is on `surface`.
 */
import type { ActionRole, Theme } from "@rista/ui/library/theme/theme";
import { defineVariants } from "@rista/ui/library/theme/variants";

export type IconButtonVariant = "ghost" | "secondary" | "primary";

const filled = (role: (t: Theme) => ActionRole) => (t: Theme) => ({
  surface: { backgroundColor: role(t).bg, borderColor: role(t).border },
  icon: { color: role(t).fg },
});

const groundOf = (t: Theme, variant: IconButtonVariant | undefined, state: "hover" | "pressed") => {
  if (variant === "primary" || variant === "secondary") return state === "hover" ? t.color.action[variant].bgHover : t.color.action[variant].bgPressed;
  return state === "hover" ? t.color.surface.hover : t.color.surface.pressed;
};

/** the icon's height in each size */
export const ICON_BUTTON_GLYPH = { sm: 20, md: 24 } as const;

export const iconButtonLook = defineVariants({
  slots: ["root", "surface", "icon"],
  base: (t) => ({
    // never shrinks: a squeezed icon button clips its picture
    root: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
    surface: { alignItems: "center", justifyContent: "center", borderRadius: t.radius.control, borderWidth: 1, borderColor: "transparent" },
    icon: { color: t.color.text.primary },
  }),
  variants: {
    variant: {
      ghost: (t) => ({ icon: { color: t.color.text.primary } }),
      secondary: filled((t) => t.color.action.secondary),
      primary: filled((t) => t.color.action.primary),
    },
    size: {
      sm: () => ({ surface: { width: 36, height: 36 } }),
      md: () => ({ surface: { width: 44, height: 44 } }),
    },
  },
  states: {
    hovered: (t, v) => ({ surface: { backgroundColor: groundOf(t, v.variant as IconButtonVariant, "hover") } }),
    focused: (t) => ({ surface: { borderColor: t.color.border.focus } }),
    pressed: (t, v) => ({ surface: { backgroundColor: groundOf(t, v.variant as IconButtonVariant, "pressed") } }),
    disabled: () => ({ root: { opacity: 0.5 } }),
  },
  defaults: { variant: "ghost", size: "md" },
});
